import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { fromEvent, Observable } from 'rxjs';
import {
  CdkOverlayOrigin,
  ConnectedPosition,
  CdkConnectedOverlay,
  ConnectedOverlayPositionChange
} from '@angular/cdk/overlay';
import {
  debounceTime,
  filter,
  mergeMap,
  switchMapTo,
  tap
} from 'rxjs/operators';
import { DOCUMENT, isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { PositionEnum } from '../../../_enums/position.enum';
import { RxjsComponent } from '../../../utils/rxjs.component';
import { SdLibConfig } from '../../../sd-lib-config';

@Component({
  selector: 'sd-core-tooltip, [sd-core-tooltip]',
  templateUrl: './core-tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CdkConnectedOverlay, NgClass, NgTemplateOutlet]
})
export class SdCoreTooltipComponent extends RxjsComponent implements OnInit {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    @Inject(DOCUMENT) private readonly document: Document,
    public sdLibConfig: SdLibConfig,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super(changeDetectorRef);
  }

  isOpened = false;

  @Input()
  tooltipClass = '';

  @Input()
  overlayOrigin: CdkOverlayOrigin;

  @ViewChild('tooltip')
  tooltip: ElementRef;

  @Output()
  newPosition: ConnectedPosition[];

  @Output()
  positionChanged = new EventEmitter();

  @Input()
  tooltipTemplate: TemplateRef<any>;

  get overlayOriginEl() {
    return this.overlayOrigin.elementRef.nativeElement;
  }

  @Input()
  position: PositionEnum;
  getPosition(position): ConnectedPosition[] {
    this.newPosition = this.getPositionsWithFallBacks(position);

    return this.newPosition;
  }

  positionChange(position: ConnectedOverlayPositionChange) {
    const originY = position.connectionPair.originY;
    const originX = position.connectionPair.originX;
    const newPosition = Object.entries(
      this.sdLibConfig?.get()?.tooltipPosition)
      .find(([, config]) => config.originX === originX && config.originY === originY)?.[0] as PositionEnum;
    this.position = newPosition || PositionEnum.default;
    this.positionChanged.emit(position);
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // open popup if mouse stopped in overlayOriginEl.
      const open$ = this.showTooltip();
      const close$ = this.hideTooltip();
      this.register(open$.pipe(switchMapTo(close$)).subscribe());
      this.getPosition(this.position);
    }
  }

  private showTooltip(): Observable<Event> {
    return this.mouseEvents().pipe(
      filter(() => !this.isOpened),
      filter((event) => this.isTarget(event)),
      tap(() => this.changeState(true)),
    );
  }

  private hideTooltip(): Observable<Event> {
    return this.mouseMovesOnElement().pipe(
      filter(() => this.isOpened),
      filter((event) => this.isMovedOutside(event)),
      tap(() => this.changeState(false)),
    );
  }

  private mouseEvents(): Observable<Event> {
    return this.mouseEnter().pipe(mergeMap(() => this.mouseMovesOnElement()));
  }

  private mouseEnter(): Observable<Event> {
    return fromEvent(this.overlayOriginEl, 'mouseenter');
  }

  private mouseMovesOnElement(): Observable<Event> {
    return fromEvent(this.document, 'mousemove').pipe(debounceTime(100));
  }

  private isMovedOutside(event): boolean {
    return !(
      this.overlayOriginEl.contains(event['target']) ||
      this.tooltip.nativeElement.contains(event['target'])
    );
  }

  private isTarget(event): boolean {
    return (
      this.overlayOriginEl === event['target'] ||
      this.overlayOriginEl.contains(event['target'])
    );
  }

  connectedOverlayDetach(): void {
    this.changeState(false);
  }

  private changeState(isOpened: boolean): void {
    this.isOpened = isOpened;
    this.changeDetectorRef.markForCheck();
  }

  getPositionsWithFallBacks(position: PositionEnum): ConnectedPosition[] {
    const POSITIONS = this.sdLibConfig?.get()?.tooltipPosition || {};

    return [POSITIONS[position], ...Object.values(POSITIONS)];
  }
}
